/* eslint-disable line-comment-position, no-inline-comments */

import {
  AccessLeg,
  Notice,
  PTRideLeg,
  ServiceCalendar,
  Trip,
} from '../../../api/clients/journey-service';
import { InterfaceResultTimetableLeg } from '../../../components/paf-result-timetable-leg/paf-result-timetable-leg.custom';
import { JSX as componentTypes } from '../../../components';
import * as levelHelpers from './levelsHelpers';
import { format } from 'date-fns/format';
import {
  formatDateStringToReadableDate,
  formatDateStringToReadableDateLongForm,
  isoDurationToWalkDuration,
} from '../../../utils/date-helper';
import { InterfaceI18nConfig } from '../i18n/i18n.interface';
import { isWithinInterval } from 'date-fns/isWithinInterval';

const dedupedNoticesForLeg = (leg: PTRideLeg): Notice[] => {
  const notices = [];

  leg.serviceJourney.notices.forEach((notice) => {
    let noticeAlreadyThere = false;

    notices.forEach((alreadyThereNotice) => {
      if (notice.name === alreadyThereNotice.name) {
        noticeAlreadyThere = true;
      }
    });

    if (!noticeAlreadyThere) {
      notices.push(notice);
    }
  });

  return notices;
};

const renderTemplateStringWithArguments = (template: string, templateArguments: Record<string, string[]>, renderInParagraph): string => {
  let returnString = template;

  Object.keys(templateArguments)
    .forEach((argumentKey) => {
      const argumentItems = templateArguments[argumentKey];

      argumentItems.forEach((argument) => {
        let argumentString = argument;
        let target = '';

        if (argumentKey === 'URL') {

          // make sure we have http or https at the beginning
          if (!argumentString.startsWith('http://') && !argumentString.startsWith('https://')) {
            argumentString = `https://${argumentString}`;
          }

          // make sure we have no trailing ;
          if (argumentString.endsWith(';')) {
            argumentString = argumentString.slice(0, -1);
          }

          // Add target _blank for external links
          if (argumentString.indexOf('post.ch') === -1 || argumentString.indexOf('postauto.ch') === -1) {
            target = 'target="_blank"';
          }

          argumentString = `<a tabindex="-1" href="${argumentString}" ${target}>${argumentString}</a>`;
        }

        if (argumentKey === 'MAIL') {
          argumentString = `<a tabindex="-1" href="mailto:${argumentString}">${argumentString}</a>`;
        }

        if (argumentKey === 'PHONE') {
          argumentString = `<a tabindex="-1" href="tel:${argumentString}">${argumentString}</a>`;
        }

        returnString = returnString.replace(argumentKey, argumentString);
      });
    });

  // api separates sentences with `;`... we render them as separate p's.
  if (renderInParagraph) {
    returnString = returnString
      .split('; ')
      .join('</p></p>');
  } else {
    returnString = returnString
      .split('; ')
      .join('<br><br>');
  }

  if (renderInParagraph) {
    return `<p>${returnString}</p>`;
  }

  return returnString;

};

const getWheelchairInfo = (leg: PTRideLeg, a11yChecked: boolean, config: InterfaceI18nConfig, language: string): InterfaceResultTimetableLeg['wheelChairInfo'] => {

  if (!a11yChecked) {
    return {};
  }

  const points = leg.serviceJourney.stopPoints;
  const arrivalPoint = points[points.length - 1];

  return {
    arrival: {
      description: arrivalPoint.accessibilityBoardingAlighting?.name || config.results.legs.wheelchair.noInformation[language],
      iconName: arrivalPoint.accessibilityBoardingAlighting?.corporateIdentityIcon || 'sa-ru',
      iconVariant: 'wheelchair',
    },
    departure: {
      description: points[0].accessibilityBoardingAlighting?.name || config.results.legs.wheelchair.noInformation[language],
      iconName: points[0].accessibilityBoardingAlighting?.corporateIdentityIcon || 'sa-ru',
      iconVariant: 'wheelchair',
    },
  };
};

const getAttributeIcons = (leg: PTRideLeg): string => {
  const attributes = [];

  dedupedNoticesForLeg(leg)
    .forEach((notice) => {
      if (notice.type === 'ATTRIBUTE') {
        attributes.push(`sa-${notice.name.toLowerCase()}`);
      }
    });

  return JSON.stringify(attributes.map((item) => ({
    name: item,
  })));
};

const getAttributeIconsForLegs = (legs: PTRideLeg[]): Notice[] => {
  const attributes: Notice[] = [];

  legs.forEach((leg) => {
    dedupedNoticesForLeg(leg)
      .forEach((notice) => {
        if (notice.type === 'ATTRIBUTE') {
          let attributeAlreadyThere = false;

          attributes.forEach((attribute) => {
            if (attribute.name === notice.name) {
              attributeAlreadyThere = true;
            }
          });

          if (!attributeAlreadyThere) {
            attributes.push(notice);
          }
        }
      });
  });

  return attributes;
};

interface InterfaceA11yItemsForLegend {
  text: string;
  icon: string;
}

const getA11yItemFromLegsWithRenderedTemplates = (legs: PTRideLeg[]): InterfaceA11yItemsForLegend => {
  const item = levelHelpers.getA11yItemFromLegs(legs);
  let returnValue: InterfaceA11yItemsForLegend;

  if (item) {
    let {
      description,
    } = item;

    if (item.assistanceService) {
      const templateArguments = item.assistanceService.arguments;
      const templateText = item.assistanceService.template;
      const assistanceService = renderTemplateStringWithArguments(templateText, templateArguments, false);

      description += `<br><br>${assistanceService}`;
    }

    let icon = '';

    if (item.corporateIdentityIcon) {
      icon = item.corporateIdentityIcon.toLocaleLowerCase();
    }

    returnValue = {
      icon,
      text: description,
    };
  }

  return returnValue;

};

const getHiddenSummaryDeparture = (leg: PTRideLeg, config: InterfaceI18nConfig, a11y: boolean, language: string): string => {
  const [departurePoint] = leg.serviceJourney.stopPoints;

  let summary = '';
  const summaryTemplates = config.results.legs.hiddenSummary;
  let departureGeneral = summaryTemplates.departure.general;

  // general
  departureGeneral = departureGeneral[language]
    .replace('{{1}}', departurePoint.place.name)
    .replace('{{2}}', format(levelHelpers.getDepartureTimeForLeg(leg), 'HH:mm'))
    .replace('{{3}}', `${levelHelpers.getTransportationNameForLeg(leg)} ${levelHelpers.getProductTextForLeg(leg)} ${levelHelpers.getDirectionForLeg(leg, config, language)}`)
    .replace('{{4}}', levelHelpers.getDeparturePlatformForLeg(leg));

  summary += departureGeneral;

  // delay
  if (departurePoint.departure?.delayText) { // to be verified
    summary += summaryTemplates.departure.delay[language].replace('{{1}}', departurePoint.departure?.delayText);
  }

  // a11y
  if (a11y && departurePoint.accessibilityBoardingAlighting?.name) {
    const templateArguments = departurePoint.accessibilityBoardingAlighting.assistanceService.arguments;
    const templateText = departurePoint.accessibilityBoardingAlighting.assistanceService.template;
    const renderedTemplate = renderTemplateStringWithArguments(templateText, templateArguments, false);

    summary += ` ${summaryTemplates.departure.a11y[language]}`;
    summary += ` ${departurePoint.accessibilityBoardingAlighting?.name}.`;
    summary += ` ${renderedTemplate}`;
  }

  // occupancy
  const firstClass = levelHelpers.getHighestOccupancyForLeg(leg, 'occupancyFirstClass');
  const secondClass = levelHelpers.getHighestOccupancyForLeg(leg, 'occupancySecondClass');

  if (firstClass !== 'UNKNOWN' && secondClass !== 'UNKNOWN') {
    summary += ` ${summaryTemplates.realtimeInfoTitle[language]}: ${levelHelpers.getOccupancyStringForOccupancies(firstClass, secondClass, config, language)}.`;
  }

  // cusHim
  const cusHimMessages = levelHelpers.getCusHimMessagesForLeg(leg);

  cusHimMessages.forEach((message, index) => {
    summary += ` ${message.description}`;

    if (index === cusHimMessages.length - 1) {
      summary += '.';
    } else {
      summary += ',';
    }
  });

  // attributes
  const notices = dedupedNoticesForLeg(leg);
  const attributeNotices = notices.filter((notice) => notice.type === 'ATTRIBUTE');

  attributeNotices.forEach((notice, index) => {
    if (index === 0) {
      summary += ` ${summaryTemplates.attributesTitle[language]}:`;
    } else {
      summary += ',';
    }

    if (notice.type === 'ATTRIBUTE') {
      summary += ` ${notice.text.template}`;
    }
  });

  return summary.replace(/<[^>]*>/gu, ' ');
};

const getHiddenSummaryArrival = (leg: PTRideLeg, config: InterfaceI18nConfig, a11y: boolean, language: string): string => {
  const points = leg.serviceJourney.stopPoints;
  const arrivalPoint = points[points.length - 1];

  let summary = '';
  const summaryTemplates = config.results.legs.hiddenSummary;
  let arrivalGeneral = summaryTemplates.arrival.general;

  // general
  arrivalGeneral = arrivalGeneral[language]
    .replace('{{1}}', arrivalPoint.place.name)
    .replace('{{2}}', format(levelHelpers.getArrivalTimeTimeForLeg(leg), 'HH:mm'))
    .replace('{{3}}', levelHelpers.getArrivalPlatformForLeg(leg));

  summary += arrivalGeneral;

  // delay
  if (arrivalPoint.arrival?.delayText) { // to be verified
    summary += summaryTemplates.arrival.delay[language].replace('{{1}}', arrivalPoint.arrival?.delayText);
  }

  // a11y
  if (a11y && arrivalPoint.accessibilityBoardingAlighting?.name) {
    const templateArguments = arrivalPoint.accessibilityBoardingAlighting.assistanceService.arguments;
    const templateText = arrivalPoint.accessibilityBoardingAlighting.assistanceService.template;
    const renderedTemplate = renderTemplateStringWithArguments(templateText, templateArguments, false);

    summary += ` ${summaryTemplates.arrival.a11y[language]}`;
    summary += ` ${arrivalPoint.accessibilityBoardingAlighting?.name}.`;
    summary += ` ${renderedTemplate}`;
  }

  return summary.replace(/<[^>]*>/gu, ' ');
};

const getUtilizationItems = (leg: PTRideLeg): any[] => {
  const occupancyFirstClass = levelHelpers.getHighestOccupancyForLeg(leg, 'occupancyFirstClass');
  const occupancySecondClass = levelHelpers.getHighestOccupancyForLeg(leg, 'occupancySecondClass');
  const utilizationItems = [];

  if (occupancyFirstClass !== 'UNKNOWN') {
    utilizationItems.push({
      travelClass: '1',
      utilization: occupancyFirstClass.toLowerCase(),
    });
  }

  if (occupancySecondClass !== 'UNKNOWN') {
    utilizationItems.push({
      travelClass: '2',
      utilization: occupancySecondClass.toLowerCase(),
    });
  }

  return utilizationItems;
};

const getExistingOccupanciesForLegs = (legs: PTRideLeg[]): string[] => {
  const occupancies = [];

  legs.forEach((leg) => {
    const occupancyFirstClass = levelHelpers.getHighestOccupancyForLeg(leg, 'occupancyFirstClass');
    const occupancySecondClass = levelHelpers.getHighestOccupancyForLeg(leg, 'occupancyFirstClass');

    if (occupancies.indexOf(occupancyFirstClass) === -1) {
      occupancies.push(occupancyFirstClass);
    }

    if (occupancies.indexOf(occupancySecondClass) === -1) {
      occupancies.push(occupancySecondClass);
    }
  });

  return occupancies;
};

// exports

export const transformLevelTwoLegData = (
  leg: PTRideLeg,
  a11yChecked: boolean,
  config: InterfaceI18nConfig,
  currentLegNumber: number,
  totalLegs: number,
  language: string,
): InterfaceResultTimetableLeg => {

  const points = leg.serviceJourney.stopPoints;
  const [departurePoint] = points;
  const arrivalPoint = points[points.length - 1];

  const legData: InterfaceResultTimetableLeg = {
    attributes: {
      config: getAttributeIcons(leg),
      level: '2',
    },

    inlineCusHim: {
      messages: levelHelpers.getCusHimMessagesForLeg(leg),
    },
    journey: {
      arrival: {
        platform: levelHelpers.getArrivalPlatformForLeg(leg),
        platformChange: arrivalPoint.arrival?.quayChanged,
        stationName: arrivalPoint.place.name,
        time: {
          delay: arrivalPoint.arrival?.delayText,
          time: levelHelpers.getArrivalTimeTimeForLeg(leg),
        },
      },
      departure: {
        platform: levelHelpers.getDeparturePlatformForLeg(leg),
        platformChange: departurePoint.departure?.quayChanged,
        stationName: departurePoint.place.name,
        time: {
          delay: departurePoint.departure?.delayText,
          time: levelHelpers.getDepartureTimeForLeg(leg),
        },
      },
    },
    pearlChain: {
      legs: JSON.stringify([
        {
          arrival: arrivalPoint.arrival?.timeAimed,
          cancelled: leg.serviceJourney.serviceAlteration.cancelled || leg.serviceJourney.serviceAlteration.partiallyCancelled, // to be verified
          departure: departurePoint.departure?.timeAimed,
        },
      ]),
      now: new Date()
        .valueOf() / 1000,
      orientation: 'vertical',
    },
    service: {
      direction: levelHelpers.getDirectionForLeg(leg, config, language),
      level: '2',
      picto: levelHelpers.getPictoForLeg(leg),
      productIcon: levelHelpers.getProductIconForLeg(leg),
      productText: levelHelpers.getProductTextForLegOptional(leg),
    },
    summary: {
      details: {
        arrival: getHiddenSummaryArrival(leg, config, a11yChecked, language),
        departure: getHiddenSummaryDeparture(leg, config, a11yChecked, language),
      },
      heading: {
        level: 5,
        title: config.results.legs.hiddenSummary.journeyPartDescription[language]
          .replace('{{1}}', currentLegNumber.toString())
          .replace('{{2}}', totalLegs.toString()),
      },
    },
    utilization: {
      config: JSON.stringify({
        utilizationItems: getUtilizationItems(leg),
      }),
      level: '2',
    },
    wheelChairInfo: getWheelchairInfo(leg, a11yChecked, config, language),
  };

  return legData;
};

export const transformLevelTwoFootpathData = (
  leg: AccessLeg,
  a11yChecked: boolean,
  config: InterfaceI18nConfig,
  language: string,
): componentTypes.PafResultFootpath => ({
  description: config.results.legs.footpath.description[language],
  time: isoDurationToWalkDuration(leg.duration),
  timeAssistiveText: config.results.legs.footpath.hiddenTextMinutes[language],
  wheelchair: a11yChecked,
});

export const transformLevelTwoAlertData = (leg: PTRideLeg, rawTrip: Trip, currentLegNumber): componentTypes.PafResultAlert[] => {
  const alerts = [];

  // trip alerts
  if (currentLegNumber === 1) {
    if (rawTrip.status.cancelled && rawTrip.status.cancelledFormatted) {
      alerts.push({
        description: rawTrip.status.cancelledFormatted,
        iconName: 'cancellation',
      });
    }

    if (rawTrip.status.alternative && rawTrip.status.alternativeFormatted) {
      alerts.push({
        description: rawTrip.status.alternativeFormatted,
        iconName: 'alternative',
      });
    }
  }

  // leg alerts
  if (leg.serviceJourney.serviceAlteration.cancelled && leg.serviceJourney.serviceAlteration.cancelledFormatted) {
    alerts.push({
      description: leg.serviceJourney.serviceAlteration.cancelledFormatted,
      iconName: 'cancellation',
    });
  } else if (leg.serviceJourney.serviceAlteration.partiallyCancelled && leg.serviceJourney.serviceAlteration.partiallyCancelledFormatted) {
    alerts.push({
      description: leg.serviceJourney.serviceAlteration.partiallyCancelledFormatted,
      iconName: 'cancellation',
    });
  }

  return alerts;
};

export const generateNotificationDataForLeg = (leg: PTRideLeg, config: InterfaceI18nConfig, language: string): componentTypes.PafNotification => {
  const dedupedNotices = levelHelpers.generateNotificationDataForLeg(leg);
  let noticeResults = '';

  dedupedNotices.forEach((dedupedNotice) => {
    noticeResults += renderTemplateStringWithArguments(dedupedNotice.text.template, dedupedNotice.text.arguments, true);
  });

  return {
    content: noticeResults,
    titleLevel: config.results.notificationReservation.titleLevel,
    titleText: config.results.notificationReservation.title[language],
  };
};

export const generateLegendDataForTrip = (trip: Trip, config: InterfaceI18nConfig, language: string, a11yChecked: boolean): componentTypes.PafResultLegendItem[] => {
  const rideLegs = levelHelpers.getRideLegs(trip.legs);
  const existingOccupancies = getExistingOccupanciesForLegs(rideLegs);
  const legendItems: componentTypes.PafResultLegendItem[] = [];

  if (existingOccupancies.indexOf('LOW') !== -1) {
    legendItems.push({
      description: config.results.hiddenRowSummary.occupancy.LOW[language],
      iconName: 'utilization-low',
      iconVariant: 'utilization',
      renderWithoutRole: false,
    });
  }

  if (existingOccupancies.indexOf('MEDIUM') !== -1) {
    legendItems.push({
      description: config.results.hiddenRowSummary.occupancy.MEDIUM[language],
      iconName: 'utilization-medium',
      iconVariant: 'utilization',
      renderWithoutRole: false,
    });
  }

  if (existingOccupancies.indexOf('HIGH') !== -1) {
    legendItems.push({
      description: config.results.hiddenRowSummary.occupancy.HIGH[language],
      iconName: 'utilization-HIGH',
      iconVariant: 'utilization',
      renderWithoutRole: false,
    });
  }

  const attributes = getAttributeIconsForLegs(rideLegs);

  attributes.forEach((attribute) => {
    legendItems.push({
      description: attribute.text.template,
      iconName: `sa-${attribute.name.toLowerCase()}`,
      iconVariant: 'attribute',
      renderWithoutRole: false,
    });
  });

  if (a11yChecked) {
    const a11y = getA11yItemFromLegsWithRenderedTemplates(rideLegs);

    if (a11y) {
      legendItems.push({
        description: a11y.text,
        iconName: a11y.icon,
        iconVariant: 'wheelchair',
        renderWithoutRole: false,
      });
    }
  }

  return legendItems;
};

export const getServiceCalendarPeriod = (serivceCalendarResponse: ServiceCalendar): string[] => {
  let period;

  serivceCalendarResponse.operatingPeriods.forEach((currentPeriod) => {
    const operatingDays = Array.from(currentPeriod.operatingDays);

    if (operatingDays.length < 2) {
      return;
    }

    const today = new Date();
    const [
      operatingFrom,
      operatingTo,
    ] = operatingDays;

    const fromDate = new Date(operatingFrom);
    const toDate = new Date(operatingTo);
    const inRange = isWithinInterval(today, {
      end: toDate,
      start: fromDate,
    });

    if (inRange) {
      period = [
        operatingFrom,
        operatingTo,
      ];
    }
  });

  return period;

};

export const generateLevel2LegendFootnote = (dates: string[], config: InterfaceI18nConfig, language: string): string => {
  const [
    from,
    to,
  ] = dates;

  let fromReadable;
  let toReadable;

  if (language === 'de' || language === 'it') {
    fromReadable = formatDateStringToReadableDate(from);
    toReadable = formatDateStringToReadableDate(to);
  } else {
    fromReadable = formatDateStringToReadableDateLongForm(from, language);
    toReadable = formatDateStringToReadableDateLongForm(to, language);
  }

  const footnote = config.results.legend.footnote[language]
    .replace('{{0}}', fromReadable)
    .replace('{{1}}', toReadable);

  return footnote;
};
